/**
 * Agent Campaign Service
 * Handles campaign management operations for AI agents
 */

import { Campaign, CampaignCreationData, CampaignMetrics } from '../../types/campaign';
import { AgentAPIRequest, AgentAPIResponse } from './AgentAPIGateway';

export interface CampaignValidationError {
  field: string;
  message: string;
  code: string;
}

export interface CampaignPermissions {
  canCreate: boolean;
  canRead: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canLaunch: boolean;
  canPause: boolean;
}

export class AgentCampaignService {
  private campaigns: Map<string, Campaign> = new Map();

  constructor() {
    this.initializeMockData();
  }

  /**
   * Create a new campaign
   */
  async createCampaign(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    const startTime = Date.now();

    try {
      // Validate permissions
      const permissions = await this.getAgentPermissions(request.agentId);
      if (!permissions.canCreate) {
        return this.createErrorResponse(request, 'INSUFFICIENT_PERMISSIONS', 'Agent does not have permission to create campaigns', 403, startTime);
      }

      // Validate request data
      const validationErrors = this.validateCampaignData(request.body);
      if (validationErrors.length > 0) {
        return this.createErrorResponse(request, 'VALIDATION_ERROR', 'Campaign data validation failed', 400, startTime, { errors: validationErrors });
      }

      // Create campaign
      const campaignData: CampaignCreationData = request.body;
      const campaign: Campaign = {
        id: this.generateCampaignId(),
        name: campaignData.name,
        description: campaignData.description,
        type: campaignData.type,
        status: 'draft',
        templateId: campaignData.templateId,
        templateName: `Template ${campaignData.templateId}`,
        segmentId: campaignData.segmentId,
        segmentName: `Segment ${campaignData.segmentId}`,
        estimatedReach: Math.floor(Math.random() * 10000) + 1000,
        metrics: this.createEmptyMetrics(),
        aiGenerated: true,
        aiAgentId: request.agentId,
        aiAgentName: `Agent ${request.agentId}`,
        aiConfidenceScore: Math.random() * 0.3 + 0.7, // 0.7-1.0
        abTestingEnabled: campaignData.abTestingEnabled || false,
        scheduledAt: campaignData.scheduledAt,
        automationTriggers: (campaignData.automationTriggers || []).map((trigger, index) => ({
          ...trigger,
          id: `trigger_${Date.now()}_${index}`
        })),
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: request.agentId,
        tags: campaignData.tags || []
      };

      // Store campaign
      this.campaigns.set(campaign.id, campaign);

      return this.createSuccessResponse(request, campaign, 201, startTime);

    } catch (error) {
      return this.createErrorResponse(request, 'INTERNAL_ERROR', error instanceof Error ? error.message : 'Internal server error', 500, startTime);
    }
  }

  /**
   * Get campaign by ID
   */
  async getCampaign(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    const startTime = Date.now();

    try {
      // Validate permissions
      const permissions = await this.getAgentPermissions(request.agentId);
      if (!permissions.canRead) {
        return this.createErrorResponse(request, 'INSUFFICIENT_PERMISSIONS', 'Agent does not have permission to read campaigns', 403, startTime);
      }

      const campaignId = request.parameters.id;
      if (!campaignId) {
        return this.createErrorResponse(request, 'MISSING_PARAMETER', 'Campaign ID is required', 400, startTime);
      }

      const campaign = this.campaigns.get(campaignId);
      if (!campaign) {
        return this.createErrorResponse(request, 'CAMPAIGN_NOT_FOUND', `Campaign with ID ${campaignId} not found`, 404, startTime);
      }

      return this.createSuccessResponse(request, campaign, 200, startTime);

    } catch (error) {
      return this.createErrorResponse(request, 'INTERNAL_ERROR', error instanceof Error ? error.message : 'Internal server error', 500, startTime);
    }
  }

  /**
   * Update campaign
   */
  async updateCampaign(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    const startTime = Date.now();

    try {
      // Validate permissions
      const permissions = await this.getAgentPermissions(request.agentId);
      if (!permissions.canUpdate) {
        return this.createErrorResponse(request, 'INSUFFICIENT_PERMISSIONS', 'Agent does not have permission to update campaigns', 403, startTime);
      }

      const campaignId = request.parameters.id;
      if (!campaignId) {
        return this.createErrorResponse(request, 'MISSING_PARAMETER', 'Campaign ID is required', 400, startTime);
      }

      const campaign = this.campaigns.get(campaignId);
      if (!campaign) {
        return this.createErrorResponse(request, 'CAMPAIGN_NOT_FOUND', `Campaign with ID ${campaignId} not found`, 404, startTime);
      }

      // Check if campaign can be updated
      if (campaign.status === 'active' || campaign.status === 'completed') {
        return this.createErrorResponse(request, 'CAMPAIGN_NOT_EDITABLE', 'Cannot update active or completed campaigns', 409, startTime);
      }

      // Validate update data
      const validationErrors = this.validateCampaignUpdateData(request.body);
      if (validationErrors.length > 0) {
        return this.createErrorResponse(request, 'VALIDATION_ERROR', 'Campaign update data validation failed', 400, startTime, { errors: validationErrors });
      }

      // Update campaign
      const updates = request.body;
      const updatedCampaign: Campaign = {
        ...campaign,
        ...updates,
        id: campaign.id, // Prevent ID changes
        updatedAt: new Date()
      };

      this.campaigns.set(campaignId, updatedCampaign);

      return this.createSuccessResponse(request, updatedCampaign, 200, startTime);

    } catch (error) {
      return this.createErrorResponse(request, 'INTERNAL_ERROR', error instanceof Error ? error.message : 'Internal server error', 500, startTime);
    }
  }

  /**
   * Delete campaign
   */
  async deleteCampaign(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    const startTime = Date.now();

    try {
      // Validate permissions
      const permissions = await this.getAgentPermissions(request.agentId);
      if (!permissions.canDelete) {
        return this.createErrorResponse(request, 'INSUFFICIENT_PERMISSIONS', 'Agent does not have permission to delete campaigns', 403, startTime);
      }

      const campaignId = request.parameters.id;
      if (!campaignId) {
        return this.createErrorResponse(request, 'MISSING_PARAMETER', 'Campaign ID is required', 400, startTime);
      }

      const campaign = this.campaigns.get(campaignId);
      if (!campaign) {
        return this.createErrorResponse(request, 'CAMPAIGN_NOT_FOUND', `Campaign with ID ${campaignId} not found`, 404, startTime);
      }

      // Check if campaign can be deleted
      if (campaign.status === 'active') {
        return this.createErrorResponse(request, 'CAMPAIGN_NOT_DELETABLE', 'Cannot delete active campaigns. Pause the campaign first.', 409, startTime);
      }

      // Delete campaign
      this.campaigns.delete(campaignId);

      return this.createSuccessResponse(request, { id: campaignId, deleted: true }, 200, startTime);

    } catch (error) {
      return this.createErrorResponse(request, 'INTERNAL_ERROR', error instanceof Error ? error.message : 'Internal server error', 500, startTime);
    }
  }

  /**
   * Launch campaign
   */
  async launchCampaign(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    const startTime = Date.now();

    try {
      // Validate permissions
      const permissions = await this.getAgentPermissions(request.agentId);
      if (!permissions.canLaunch) {
        return this.createErrorResponse(request, 'INSUFFICIENT_PERMISSIONS', 'Agent does not have permission to launch campaigns', 403, startTime);
      }

      const campaignId = request.parameters.id;
      if (!campaignId) {
        return this.createErrorResponse(request, 'MISSING_PARAMETER', 'Campaign ID is required', 400, startTime);
      }

      const campaign = this.campaigns.get(campaignId);
      if (!campaign) {
        return this.createErrorResponse(request, 'CAMPAIGN_NOT_FOUND', `Campaign with ID ${campaignId} not found`, 404, startTime);
      }

      // Check if campaign can be launched
      if (campaign.status !== 'draft' && campaign.status !== 'paused' && campaign.status !== 'scheduled') {
        return this.createErrorResponse(request, 'CAMPAIGN_NOT_LAUNCHABLE', `Cannot launch campaign with status: ${campaign.status}`, 409, startTime);
      }

      // Validate campaign is ready for launch
      const launchValidation = this.validateCampaignForLaunch(campaign);
      if (launchValidation.length > 0) {
        return this.createErrorResponse(request, 'CAMPAIGN_NOT_READY', 'Campaign is not ready for launch', 400, startTime, { errors: launchValidation });
      }

      // Launch campaign
      const launchedCampaign: Campaign = {
        ...campaign,
        status: 'active',
        sentAt: new Date(),
        updatedAt: new Date()
      };

      this.campaigns.set(campaignId, launchedCampaign);

      return this.createSuccessResponse(request, {
        id: campaignId,
        status: 'active',
        message: 'Campaign launched successfully',
        launchedAt: launchedCampaign.sentAt
      }, 200, startTime);

    } catch (error) {
      return this.createErrorResponse(request, 'INTERNAL_ERROR', error instanceof Error ? error.message : 'Internal server error', 500, startTime);
    }
  }

  /**
   * Pause campaign
   */
  async pauseCampaign(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    const startTime = Date.now();

    try {
      // Validate permissions
      const permissions = await this.getAgentPermissions(request.agentId);
      if (!permissions.canPause) {
        return this.createErrorResponse(request, 'INSUFFICIENT_PERMISSIONS', 'Agent does not have permission to pause campaigns', 403, startTime);
      }

      const campaignId = request.parameters.id;
      if (!campaignId) {
        return this.createErrorResponse(request, 'MISSING_PARAMETER', 'Campaign ID is required', 400, startTime);
      }

      const campaign = this.campaigns.get(campaignId);
      if (!campaign) {
        return this.createErrorResponse(request, 'CAMPAIGN_NOT_FOUND', `Campaign with ID ${campaignId} not found`, 404, startTime);
      }

      // Check if campaign can be paused
      if (campaign.status !== 'active' && campaign.status !== 'scheduled') {
        return this.createErrorResponse(request, 'CAMPAIGN_NOT_PAUSABLE', `Cannot pause campaign with status: ${campaign.status}`, 409, startTime);
      }

      // Pause campaign
      const pausedCampaign: Campaign = {
        ...campaign,
        status: 'paused',
        updatedAt: new Date()
      };

      this.campaigns.set(campaignId, pausedCampaign);

      return this.createSuccessResponse(request, {
        id: campaignId,
        status: 'paused',
        message: 'Campaign paused successfully',
        pausedAt: pausedCampaign.updatedAt
      }, 200, startTime);

    } catch (error) {
      return this.createErrorResponse(request, 'INTERNAL_ERROR', error instanceof Error ? error.message : 'Internal server error', 500, startTime);
    }
  }

  /**
   * Get campaign status
   */
  async getCampaignStatus(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    const startTime = Date.now();

    try {
      // Validate permissions
      const permissions = await this.getAgentPermissions(request.agentId);
      if (!permissions.canRead) {
        return this.createErrorResponse(request, 'INSUFFICIENT_PERMISSIONS', 'Agent does not have permission to read campaign status', 403, startTime);
      }

      const campaignId = request.parameters.id;
      if (!campaignId) {
        return this.createErrorResponse(request, 'MISSING_PARAMETER', 'Campaign ID is required', 400, startTime);
      }

      const campaign = this.campaigns.get(campaignId);
      if (!campaign) {
        return this.createErrorResponse(request, 'CAMPAIGN_NOT_FOUND', `Campaign with ID ${campaignId} not found`, 404, startTime);
      }

      const statusInfo = {
        id: campaign.id,
        name: campaign.name,
        status: campaign.status,
        type: campaign.type,
        createdAt: campaign.createdAt,
        updatedAt: campaign.updatedAt,
        scheduledAt: campaign.scheduledAt,
        sentAt: campaign.sentAt,
        estimatedReach: campaign.estimatedReach,
        metrics: campaign.metrics,
        aiConfidenceScore: campaign.aiConfidenceScore
      };

      return this.createSuccessResponse(request, statusInfo, 200, startTime);

    } catch (error) {
      return this.createErrorResponse(request, 'INTERNAL_ERROR', error instanceof Error ? error.message : 'Internal server error', 500, startTime);
    }
  }

  /**
   * List campaigns with filtering
   */
  async listCampaigns(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    const startTime = Date.now();

    try {
      // Validate permissions
      const permissions = await this.getAgentPermissions(request.agentId);
      if (!permissions.canRead) {
        return this.createErrorResponse(request, 'INSUFFICIENT_PERMISSIONS', 'Agent does not have permission to list campaigns', 403, startTime);
      }

      const { status, type, limit = 50, offset = 0 } = request.parameters;
      
      let campaigns = Array.from(this.campaigns.values());

      // Filter by agent's campaigns only
      campaigns = campaigns.filter(c => c.aiAgentId === request.agentId);

      // Apply filters
      if (status) {
        campaigns = campaigns.filter(c => c.status === status);
      }
      if (type) {
        campaigns = campaigns.filter(c => c.type === type);
      }

      // Apply pagination
      const total = campaigns.length;
      const paginatedCampaigns = campaigns.slice(offset, offset + limit);

      return this.createSuccessResponse(request, {
        campaigns: paginatedCampaigns,
        pagination: {
          total,
          limit,
          offset,
          hasMore: offset + limit < total
        }
      }, 200, startTime);

    } catch (error) {
      return this.createErrorResponse(request, 'INTERNAL_ERROR', error instanceof Error ? error.message : 'Internal server error', 500, startTime);
    }
  }

  // Private helper methods

  private async getAgentPermissions(_agentId: string): Promise<CampaignPermissions> {
    // In a real implementation, this would fetch from the agent registration service
    // For now, return full permissions for all agents
    return {
      canCreate: true,
      canRead: true,
      canUpdate: true,
      canDelete: true,
      canLaunch: true,
      canPause: true
    };
  }

  private validateCampaignData(data: any): CampaignValidationError[] {
    const errors: CampaignValidationError[] = [];

    if (!data.name || typeof data.name !== 'string' || data.name.trim().length === 0) {
      errors.push({ field: 'name', message: 'Campaign name is required', code: 'REQUIRED' });
    } else if (data.name.length > 100) {
      errors.push({ field: 'name', message: 'Campaign name must be 100 characters or less', code: 'MAX_LENGTH' });
    }

    if (!data.type || !['promotional', 'automated', 'announcement', 'sequence', 'welcome', 'newsletter'].includes(data.type)) {
      errors.push({ field: 'type', message: 'Valid campaign type is required', code: 'INVALID_VALUE' });
    }

    if (!data.templateId || typeof data.templateId !== 'string') {
      errors.push({ field: 'templateId', message: 'Template ID is required', code: 'REQUIRED' });
    }

    if (!data.segmentId || typeof data.segmentId !== 'string') {
      errors.push({ field: 'segmentId', message: 'Segment ID is required', code: 'REQUIRED' });
    }

    if (data.scheduledAt && !(data.scheduledAt instanceof Date) && isNaN(Date.parse(data.scheduledAt))) {
      errors.push({ field: 'scheduledAt', message: 'Invalid scheduled date format', code: 'INVALID_FORMAT' });
    }

    if (data.tags && !Array.isArray(data.tags)) {
      errors.push({ field: 'tags', message: 'Tags must be an array', code: 'INVALID_TYPE' });
    }

    return errors;
  }

  private validateCampaignUpdateData(data: any): CampaignValidationError[] {
    const errors: CampaignValidationError[] = [];

    if (data.name !== undefined) {
      if (typeof data.name !== 'string' || data.name.trim().length === 0) {
        errors.push({ field: 'name', message: 'Campaign name must be a non-empty string', code: 'INVALID_VALUE' });
      } else if (data.name.length > 100) {
        errors.push({ field: 'name', message: 'Campaign name must be 100 characters or less', code: 'MAX_LENGTH' });
      }
    }

    if (data.type !== undefined && !['promotional', 'automated', 'announcement', 'sequence', 'welcome', 'newsletter'].includes(data.type)) {
      errors.push({ field: 'type', message: 'Invalid campaign type', code: 'INVALID_VALUE' });
    }

    if (data.scheduledAt !== undefined && !(data.scheduledAt instanceof Date) && isNaN(Date.parse(data.scheduledAt))) {
      errors.push({ field: 'scheduledAt', message: 'Invalid scheduled date format', code: 'INVALID_FORMAT' });
    }

    if (data.tags !== undefined && !Array.isArray(data.tags)) {
      errors.push({ field: 'tags', message: 'Tags must be an array', code: 'INVALID_TYPE' });
    }

    return errors;
  }

  private validateCampaignForLaunch(campaign: Campaign): CampaignValidationError[] {
    const errors: CampaignValidationError[] = [];

    if (!campaign.templateId) {
      errors.push({ field: 'templateId', message: 'Campaign must have a template', code: 'MISSING_TEMPLATE' });
    }

    if (!campaign.segmentId) {
      errors.push({ field: 'segmentId', message: 'Campaign must have a target segment', code: 'MISSING_SEGMENT' });
    }

    if (campaign.estimatedReach === 0) {
      errors.push({ field: 'estimatedReach', message: 'Campaign has no target audience', code: 'NO_AUDIENCE' });
    }

    return errors;
  }

  private createEmptyMetrics(): CampaignMetrics {
    return {
      sent: 0,
      delivered: 0,
      opened: 0,
      clicked: 0,
      converted: 0,
      revenue: 0,
      openRate: 0,
      clickRate: 0,
      conversionRate: 0,
      roi: 0,
      bounceRate: 0,
      unsubscribeRate: 0
    };
  }

  private generateCampaignId(): string {
    return `campaign_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  private createSuccessResponse(request: AgentAPIRequest, data: any, status: number, startTime: number): AgentAPIResponse {
    return {
      status,
      headers: {},
      data,
      duration: Date.now() - startTime,
      requestId: request.requestId,
      success: true,
      metadata: {
        requestId: request.requestId,
        timestamp: new Date(),
        processingTime: Date.now() - startTime,
        rateLimit: {
          remaining: 999, // Mock rate limit
          resetTime: new Date(Date.now() + 60000)
        }
      }
    };
  }

  private createErrorResponse(
    request: AgentAPIRequest, 
    code: string, 
    message: string, 
    status: number, 
    startTime: number, 
    details?: any
  ): AgentAPIResponse {
    return {
      status,
      headers: {},
      data: null,
      duration: Date.now() - startTime,
      requestId: request.requestId,
      success: false,
      error: {
        code,
        message,
        details: {
          ...details,
          timestamp: new Date(),
          requestId: request.requestId,
          documentation: `https://docs.example.com/api/errors#${code.toLowerCase()}`
        }
      },
      metadata: {
        requestId: request.requestId,
        timestamp: new Date(),
        processingTime: Date.now() - startTime,
        rateLimit: {
          remaining: 999,
          resetTime: new Date(Date.now() + 60000)
        }
      }
    };
  }

  private initializeMockData(): void {
    // Create some mock campaigns for testing
    const mockCampaigns: Campaign[] = [
      {
        id: 'campaign_mock_1',
        name: 'Welcome Email Series',
        description: 'Automated welcome email sequence for new subscribers',
        type: 'welcome',
        status: 'active',
        templateId: 'template_1',
        templateName: 'Welcome Template',
        segmentId: 'segment_1',
        segmentName: 'New Subscribers',
        estimatedReach: 1500,
        metrics: {
          sent: 1200,
          delivered: 1180,
          opened: 354,
          clicked: 71,
          converted: 14,
          revenue: 420,
          openRate: 30.0,
          clickRate: 20.0,
          conversionRate: 4.0,
          roi: 300,
          bounceRate: 1.7,
          unsubscribeRate: 0.5
        },
        aiGenerated: true,
        aiAgentId: 'agent_test_1',
        aiAgentName: 'Test Agent 1',
        aiConfidenceScore: 0.85,
        abTestingEnabled: false,
        sentAt: new Date(Date.now() - 86400000), // 1 day ago
        createdAt: new Date(Date.now() - 172800000), // 2 days ago
        updatedAt: new Date(Date.now() - 86400000),
        createdBy: 'agent_test_1',
        tags: ['welcome', 'automated']
      },
      {
        id: 'campaign_mock_2',
        name: 'Product Launch Announcement',
        description: 'Announcing our new product features',
        type: 'announcement',
        status: 'draft',
        templateId: 'template_2',
        templateName: 'Announcement Template',
        segmentId: 'segment_2',
        segmentName: 'All Customers',
        estimatedReach: 5000,
        metrics: this.createEmptyMetrics(),
        aiGenerated: true,
        aiAgentId: 'agent_test_1',
        aiAgentName: 'Test Agent 1',
        aiConfidenceScore: 0.92,
        abTestingEnabled: true,
        scheduledAt: new Date(Date.now() + 86400000), // Tomorrow
        createdAt: new Date(Date.now() - 3600000), // 1 hour ago
        updatedAt: new Date(Date.now() - 1800000), // 30 minutes ago
        createdBy: 'agent_test_1',
        tags: ['product', 'announcement']
      }
    ];

    mockCampaigns.forEach(campaign => {
      this.campaigns.set(campaign.id, campaign);
    });
  }
}

// Export singleton instance
export const agentCampaignService = new AgentCampaignService();